import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

interface Product {
  id: string;
  title: string;
  price: number;
  image: string;
  category: string;
  rating: number;
  reviews: number;
}

const HomePage = () => {
  const { user } = useAuth();
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Mock featured products
    const mockProducts: Product[] = [
      {
        id: '1',
        title: 'Premium Wireless Headphones',
        price: 299.99,
        image: 'https://public.readdy.ai/ai/img_res/c60fc724c83a07829e305a57f80fdb15.jpg',
        category: 'Electronics',
        rating: 4.8,
        reviews: 234
      },
      {
        id: '2',
        title: 'Minimalist Watch Collection',
        price: 189.99,
        image: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20wristwatch%20with%20clean%20dial%20and%20leather%20strap%20on%20pure%20white%20background%20with%20professional%20product%20lighting%20highlighting%20the%20refined%20details%20and%20premium%20craftsmanship%20of%20luxury%20timepiece&width=400&height=400&seq=prod2&orientation=squarish',
        category: 'Fashion',
        rating: 4.9,
        reviews: 456
      },
      {
        id: '3',
        title: 'Smart Fitness Tracker',
        price: 149.99,
        image: 'https://readdy.ai/api/search-image?query=Modern%20fitness%20tracker%20smartwatch%20with%20vibrant%20display%20on%20clean%20white%20background%20with%20studio%20lighting%20showcasing%20sleek%20design%20and%20advanced%20technology%20features%20perfect%20for%20active%20lifestyle%20product%20photography&width=400&height=400&seq=prod3&orientation=squarish',
        category: 'Electronics',
        rating: 4.7,
        reviews: 189
      },
      {
        id: '4',
        title: 'Designer Sunglasses',
        price: 129.99,
        image: 'https://readdy.ai/api/search-image?query=Stylish%20designer%20sunglasses%20with%20modern%20frame%20on%20pure%20white%20background%20with%20professional%20studio%20lighting%20highlighting%20premium%20materials%20and%20contemporary%20fashion%20design%20perfect%20for%20luxury%20product%20photography&width=400&height=400&seq=prod4&orientation=squarish',
        category: 'Fashion',
        rating: 4.6,
        reviews: 312
      },
      {
        id: '5',
        title: 'Portable Bluetooth Speaker',
        price: 79.99,
        image: 'https://readdy.ai/api/search-image?query=Compact%20portable%20bluetooth%20speaker%20with%20modern%20design%20on%20clean%20white%20background%20with%20studio%20lighting%20showcasing%20premium%20build%20quality%20and%20sleek%20aesthetic%20perfect%20for%20tech%20product%20photography&width=400&height=400&seq=prod5&orientation=squarish',
        category: 'Electronics',
        rating: 4.5,
        reviews: 567
      },
      {
        id: '6',
        title: 'Leather Crossbody Bag',
        price: 159.99,
        image: 'https://readdy.ai/api/search-image?query=Premium%20leather%20crossbody%20bag%20with%20elegant%20design%20on%20pure%20white%20background%20with%20professional%20studio%20lighting%20highlighting%20fine%20craftsmanship%20and%20luxurious%20texture%20perfect%20for%20fashion%20product%20photography&width=400&height=400&seq=prod6&orientation=squarish',
        category: 'Fashion',
        rating: 4.8,
        reviews: 423
      }
    ];

    setFeaturedProducts(mockProducts);
    setLoading(false);
    setIsVisible(true);
  }, []);

  const categories = [
    { name: 'Electronics', icon: 'ri-smartphone-line', color: 'from-blue-500 to-cyan-500' },
    { name: 'Fashion', icon: 'ri-shirt-line', color: 'from-pink-500 to-rose-500' },
    { name: 'Home & Living', icon: 'ri-home-4-line', color: 'from-green-500 to-emerald-500' },
    { name: 'Beauty', icon: 'ri-heart-pulse-line', color: 'from-purple-500 to-pink-500' },
    { name: 'Sports', icon: 'ri-basketball-line', color: 'from-orange-500 to-red-500' },
    { name: 'Books', icon: 'ri-book-open-line', color: 'from-indigo-500 to-purple-500' }
  ];

  const filteredProducts = selectedCategory === 'all' 
    ? featuredProducts 
    : featuredProducts.filter(product => product.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-20 pb-16 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url(https://readdy.ai/api/search-image?query=modern%20minimalist%20abstract%20geometric%20shapes%20soft%20pastel%20pink%20purple%20gradient%20background%20clean%20elegant%20design%20professional%20ecommerce%20shopping%20aesthetic%20high%20quality%20digital%20art%20smooth%20flowing%20lines%20contemporary%20style&width=1920&height=800&seq=hero-bg-001&orientation=landscape)',
          }}
        ></div>

        <div className={`relative max-w-7xl mx-auto text-center py-20 w-full transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h1 className="text-5xl md:text-7xl font-display font-bold text-white mb-6 drop-shadow-lg">
            ShopHub'a Hoş Geldiniz
          </h1>
          <p className="text-xl md:text-2xl text-white mb-8 drop-shadow-md max-w-3xl mx-auto">
            En kaliteli ürünleri en uygun fiyatlarla keşfedin
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => {
                const productsSection = document.getElementById('products');
                productsSection?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-8 py-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl font-semibold hover:from-pink-600 hover:to-purple-700 transition-all shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Ürünleri İncele
            </button>
            <button
              onClick={() => window.REACT_APP_NAVIGATE && window.REACT_APP_NAVIGATE('/yonetici')}
              className="px-8 py-4 bg-white text-gray-900 rounded-xl font-semibold hover:bg-gray-100 transition-all shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Yönetici Paneli
            </button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: 'ri-truck-line', title: 'Ücretsiz Kargo', desc: 'Tüm siparişlerde ücretsiz ve hızlı teslimat', delay: '0' },
              { icon: 'ri-shield-check-line', title: 'Güvenli Ödeme', desc: '256-bit SSL ile korunan güvenli ödeme sistemi', delay: '100' },
              { icon: 'ri-arrow-go-back-line', title: 'Kolay İade', desc: '14 gün içinde koşulsuz iade garantisi', delay: '200' }
            ].map((feature, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 animate-fade-in-up"
                style={{ animationDelay: `${feature.delay}ms` }}
              >
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-pink-100 to-purple-100 rounded-full mb-4">
                  <i className={`${feature.icon} text-3xl text-pink-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products */}
      <section id="products" className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-display font-bold text-gray-900">Öne Çıkan Ürünler</h2>
            <div className="flex gap-2 overflow-x-auto">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap cursor-pointer ${
                  selectedCategory === 'all'
                    ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                Tümü
              </button>
              {categories.map((category) => (
                <button
                  key={category.name}
                  onClick={() => setSelectedCategory(category.name)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap cursor-pointer ${
                    selectedCategory === category.name
                      ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 border-4 border-pink-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Ürünler yükleniyor...</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">Bu kategoride henüz ürün bulunmuyor.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-product-shop>
              {filteredProducts.map((product, idx) => (
                <div
                  key={product.id}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group cursor-pointer animate-fade-in-up hover:scale-105"
                  style={{ animationDelay: `${idx * 50}ms` }}
                  onClick={() => window.REACT_APP_NAVIGATE && window.REACT_APP_NAVIGATE(`/urun/${product.id}`)}
                >
                  <div className="relative aspect-square w-full overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-3 right-3 px-3 py-1 bg-gradient-to-r from-pink-500 to-purple-600 text-white text-sm font-semibold rounded-full">
                      {product.category}
                    </div>
                  </div>

                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                      {product.title}
                    </h3>

                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <i
                            key={i}
                            className={`ri-star-${i < Math.floor(product.rating) ? 'fill' : 'line'} text-sm ${
                              i < Math.floor(product.rating) ? 'text-yellow-400' : 'text-gray-300'
                            }`}
                          ></i>
                        ))}
                      </div>
                      <span className="text-xs text-gray-500">({product.reviews})</span>
                    </div>

                    <div className="flex items-center justify-between mb-3">
                      <p className="text-2xl font-bold text-pink-600">${product.price.toFixed(2)}</p>
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        window.REACT_APP_NAVIGATE && window.REACT_APP_NAVIGATE(`/urun/${product.id}`);
                      }}
                      className="w-full px-4 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl font-semibold hover:from-pink-600 hover:to-purple-700 transition-all whitespace-nowrap cursor-pointer"
                    >
                      İncele
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-display font-bold text-gray-900 mb-4">Kategorilere Göz Atın</h2>
            <p className="text-lg text-gray-600">Geniş ürün yelpazemizi keşfedin</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6" data-product-shop>
            {categories.map((category, idx) => (
              <button
                key={category.name}
                onClick={() => window.REACT_APP_NAVIGATE && window.REACT_APP_NAVIGATE('/arama')}
                className="group relative overflow-hidden rounded-2xl p-8 bg-gradient-to-br hover:shadow-2xl transition-all duration-300 transform hover:scale-105 cursor-pointer animate-fade-in-up"
                style={{ 
                  background: `linear-gradient(135deg, var(--tw-gradient-stops))`,
                  animationDelay: `${idx * 100}ms`
                }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${category.color} opacity-90 group-hover:opacity-100 transition-opacity`}></div>
                <div className="relative z-10 text-center">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-3">
                    <i className={`${category.icon} text-4xl text-white`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-white">{category.name}</h3>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-display font-bold text-gray-900 mb-4">Özel Seçkiler</h2>
            <p className="text-lg text-gray-600">Sizin için özenle seçilmiş ürünler</p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-gray-500">Ürünler yükleniyor...</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-product-shop>
              {featuredProducts.map((product, idx) => (
                <div
                  key={product.id}
                  className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 cursor-pointer animate-fade-in-up"
                  style={{ animationDelay: `${idx * 100}ms` }}
                  onClick={() => window.REACT_APP_NAVIGATE && window.REACT_APP_NAVIGATE(`/urun/${product.id}`)}
                >
                  <div className="relative overflow-hidden bg-gray-100">
                    <div className="w-full h-80">
                      <img
                        src={product.image}
                        alt={product.title}
                        className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                      <i className="ri-heart-line text-xl text-gray-700 hover:text-red-500 transition-colors"></i>
                    </div>
                    <div className="absolute top-4 left-4 px-3 py-1 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-xs font-semibold rounded-full">
                      {product.category}
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                      {product.title}
                    </h3>
                    
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <i
                            key={i}
                            className={`ri-star-${i < Math.floor(product.rating) ? 'fill' : 'line'} text-sm ${
                              i < Math.floor(product.rating) ? 'text-yellow-400' : 'text-gray-300'
                            }`}
                          ></i>
                        ))}
                      </div>
                      <span className="text-xs text-gray-500">({product.reviews})</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-gray-900">${product.price}</span>
                      <button className="px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-sm font-semibold rounded-full hover:from-teal-600 hover:to-cyan-600 transition-all whitespace-nowrap cursor-pointer">
                        Sepete Ekle
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <button
              onClick={() => window.REACT_APP_NAVIGATE && window.REACT_APP_NAVIGATE('/arama')}
              className="px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-lg font-semibold rounded-full hover:from-teal-600 hover:to-cyan-600 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Tüm Ürünleri Görüntüle
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: 'ri-truck-line', color: 'from-teal-500 to-cyan-500', bg: 'from-teal-50 to-cyan-50', title: 'Hızlı Teslimat', desc: '$50 üzeri siparişlerde ücretsiz kargo', delay: '0' },
              { icon: 'ri-shield-check-line', color: 'from-purple-500 to-pink-500', bg: 'from-purple-50 to-pink-50', title: 'Güvenli Ödeme', desc: '%100 güvenli işlemler', delay: '100' },
              { icon: 'ri-customer-service-2-line', color: 'from-orange-500 to-red-500', bg: 'from-orange-50 to-red-50', title: '7/24 Destek', desc: 'Her zaman yanınızdayız', delay: '200' }
            ].map((feature, idx) => (
              <div
                key={idx}
                className={`text-center p-8 rounded-2xl bg-gradient-to-br ${feature.bg} animate-fade-in-up hover:scale-105 transition-transform duration-300`}
                style={{ animationDelay: `${feature.delay}ms` }}
              >
                <div className={`w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-gradient-to-r ${feature.color} rounded-full`}>
                  <i className={`${feature.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-gradient-to-r from-teal-500 to-cyan-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-display font-bold text-white mb-4">Stay Updated</h2>
          <p className="text-lg text-white/90 mb-8">Subscribe to get special offers, free giveaways, and updates</p>
          
          <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto" onSubmit={(e) => {
            e.preventDefault();
            const emailInput = e.currentTarget.querySelector('input[type="email"]') as HTMLInputElement;
            if (emailInput && emailInput.value) {
              console.log('Subscribed with email:', emailInput.value);
              alert('Thank you for subscribing!');
              emailInput.value = '';
            }
          }}>
            <input
              type="email"
              placeholder="Enter your email"
              required
              className="flex-1 px-6 py-4 text-sm rounded-full focus:ring-4 focus:ring-white/30 focus:outline-none"
            />
            <button
              type="submit"
              className="px-8 py-4 bg-white text-teal-600 text-sm font-semibold rounded-full hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Subscribe
            </button>
          </form>
        </div>
      </section>

      <Footer />

      <style>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};

export default HomePage;
